/**
 * 대리인(Agent) 생성/편집 모달 (M13 / F-4).
 *
 * 단일 컴포넌트로 두 모드를 모두 처리:
 *   - mode="create" → POST /api/admin/agents, id 입력 가능.
 *   - mode="edit"   → PATCH /api/admin/agents/{id}, id readonly + dirty tracking.
 *
 * 변경 필드만 PATCH 페이로드에 포함하여 백엔드의
 *   "변경할 필드가 없습니다." (400) 응답을 회피한다.
 *
 * keywords: chip 입력 (Enter / 콤마로 추가, X 로 제거, 백스페이스로 마지막 제거).
 * extraction_rules: 자유 JSON textarea (선택). 빈 문자열 → null.
 */

import { useEffect, useMemo, useState } from "react";
import { Loader2, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { LLM_FIELD_META_MAP } from "@/features/records/fieldLabels";
import type {
  AgentCreatePayload,
  AgentExtractionRules,
  AgentOut,
  AgentReviewRules,
  AgentUpdatePayload,
} from "@/lib/api/agents";
import { ApiError } from "@/lib/api/client";

import ExtractionRulesEditor, { type RulesMode } from "./ExtractionRulesEditor";
import { useCreateAgent, useUpdateAgent } from "./useAgents";

const AGENT_ID_PATTERN = /^[A-Za-z0-9_-]+$/;
const AGENT_ID_MAX_LEN = 10;
const PRIORITY_MIN = 0;
const PRIORITY_MAX = 10000;

// ────────────── 보조: keywords / extraction_rules ──────────────

function normalizeKeyword(value: string): string {
  return value.trim();
}

function keywordsEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((v, i) => v === b[i]);
}

function rulesEqual(a: AgentExtractionRules | null, b: AgentExtractionRules | null): boolean {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

function reviewRulesEqual(a: AgentReviewRules | null, b: AgentReviewRules | null): boolean {
  const an = a && Object.keys(a).length > 0 ? a : null;
  const bn = b && Object.keys(b).length > 0 ? b : null;
  if (an === null && bn === null) return true;
  if (an === null || bn === null) return false;
  return JSON.stringify(an) === JSON.stringify(bn);
}

interface ParsedRules {
  value: AgentExtractionRules | null;
  error: string | null;
}

function parseExtractionRules(raw: string, errorMsg: string): ParsedRules {
  const trimmed = raw.trim();
  if (!trimmed) return { value: null, error: null };
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (parsed === null) return { value: null, error: null };
    if (typeof parsed !== "object" || Array.isArray(parsed)) {
      return { value: null, error: errorMsg };
    }
    return { value: parsed as AgentExtractionRules, error: null };
  } catch {
    return { value: null, error: errorMsg };
  }
}

// ────────────── Props ──────────────

interface CreateProps {
  mode: "create";
  onClose: () => void;
}

interface EditProps {
  mode: "edit";
  agent: AgentOut;
  onClose: () => void;
}

type Props = CreateProps | EditProps;

// ────────────── 컴포넌트 ──────────────

export default function AgentFormModal(props: Props) {
  const { t } = useTranslation();
  const isEdit = props.mode === "edit";
  const initial = isEdit ? props.agent : null;

  const createMu = useCreateAgent();
  const updateMu = useUpdateAgent();
  const mutation = isEdit ? updateMu : createMu;

  // ────────────── 폼 상태 ──────────────
  const [id, setId] = useState(initial?.id ?? "");
  const [officialName, setOfficialName] = useState(initial?.official_name ?? "");
  const [currency, setCurrency] = useState(initial?.currency ?? "");
  const [keywords, setKeywords] = useState<string[]>(initial?.keywords ?? []);
  const [keywordInput, setKeywordInput] = useState("");
  const [rulesText, setRulesText] = useState(
    initial?.extraction_rules ? JSON.stringify(initial.extraction_rules, null, 2) : "",
  );
  const [rulesMode, setRulesMode] = useState<RulesMode>("form");
  const [reviewRules, setReviewRules] = useState<AgentReviewRules | null>(
    initial?.review_rules ?? null,
  );
  const [priority, setPriority] = useState<number>(initial?.priority ?? 50);
  const [withholdingEnabled, setWithholdingEnabled] = useState(
    initial?.withholding_enabled ?? false,
  );
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);

  // ESC 닫기
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") props.onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [props]);

  // ────────────── extraction_rules parse ──────────────
  const parsedRules = useMemo(
    () => parseExtractionRules(rulesText, t("admin.agents.form.rulesError")),
    [rulesText, t],
  );

  // ────────────── 검증 ──────────────
  const idValid = !isEdit
    ? id.length > 0 && id.length <= AGENT_ID_MAX_LEN && AGENT_ID_PATTERN.test(id)
    : true;
  const nameValid = officialName.trim().length > 0;
  const priorityValid =
    Number.isFinite(priority) && priority >= PRIORITY_MIN && priority <= PRIORITY_MAX;
  const rulesValid = parsedRules.error === null;

  const formValid = idValid && nameValid && priorityValid && rulesValid;

  // ────────────── dirty tracking (edit only) ──────────────
  const normalizedCurrency = currency.trim().toUpperCase();
  const changedFields = useMemo<AgentUpdatePayload>(() => {
    if (!initial) return {};
    const out: AgentUpdatePayload = {};
    if (officialName !== initial.official_name) out.official_name = officialName;
    if ((normalizedCurrency || null) !== (initial.currency ?? null)) {
      out.currency = normalizedCurrency || null;
    }
    if (!keywordsEqual(keywords, initial.keywords)) out.keywords = keywords;
    if (!rulesEqual(parsedRules.value, initial.extraction_rules)) {
      out.extraction_rules = parsedRules.value;
    }
    if (!reviewRulesEqual(reviewRules, initial.review_rules)) {
      out.review_rules = reviewRules ?? {};
    }
    if (priority !== initial.priority) out.priority = priority;
    if (withholdingEnabled !== initial.withholding_enabled) {
      out.withholding_enabled = withholdingEnabled;
    }
    if (isActive !== initial.is_active) out.is_active = isActive;
    return out;
  }, [
    initial,
    officialName,
    normalizedCurrency,
    keywords,
    parsedRules.value,
    reviewRules,
    priority,
    withholdingEnabled,
    isActive,
  ]);

  const isDirty = !initial || Object.keys(changedFields).length > 0;
  const canSubmit = formValid && isDirty && !mutation.isPending;

  // ────────────── keywords chip 핸들러 ──────────────
  const addKeyword = (raw: string) => {
    const k = normalizeKeyword(raw);
    if (!k) return;
    if (keywords.includes(k)) {
      setKeywordInput("");
      return;
    }
    setKeywords([...keywords, k]);
    setKeywordInput("");
  };

  const handleKeywordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addKeyword(keywordInput);
    } else if (e.key === "Backspace" && keywordInput === "" && keywords.length > 0) {
      setKeywords(keywords.slice(0, -1));
    }
  };

  const handleKeywordBlur = () => {
    if (keywordInput.trim()) addKeyword(keywordInput);
  };

  const removeKeyword = (idx: number) => {
    setKeywords(keywords.filter((_, i) => i !== idx));
  };

  // ────────────── review_rules (읽기 전용 + 삭제) ──────────────
  const reviewEntries = useMemo(() => Object.entries(reviewRules ?? {}), [reviewRules]);
  const removeReviewRule = (field: string) => {
    setReviewRules((prev) => {
      if (!prev) return prev;
      return Object.fromEntries(Object.entries(prev).filter(([k]) => k !== field));
    });
  };
  const fieldLabel = (field: string): string => {
    const meta = LLM_FIELD_META_MAP[field];
    return meta ? t(meta.labelI18nKey) : field;
  };

  // ────────────── submit ──────────────
  const apiError =
    mutation.error instanceof ApiError ? mutation.error.detail : (mutation.error?.message ?? null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    if (isEdit) {
      updateMu.mutate(
        { agentId: props.agent.id, payload: changedFields },
        { onSuccess: () => props.onClose() },
      );
    } else {
      const payload: AgentCreatePayload = {
        id,
        official_name: officialName,
        currency: normalizedCurrency || null,
        keywords,
        extraction_rules: parsedRules.value,
        priority,
        withholding_enabled: withholdingEnabled,
        is_active: isActive,
      };
      createMu.mutate(payload, { onSuccess: () => props.onClose() });
    }
  };

  const title = isEdit
    ? t("admin.agents.form.editTitle", { id: props.agent.id })
    : t("admin.agents.form.createTitle");
  const submitLabel = isEdit
    ? t("admin.agents.form.submitEdit")
    : t("admin.agents.form.submitCreate");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4 py-8"
      onClick={props.onClose}
    >
      <div
        className="w-full max-w-xl rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          <button
            type="button"
            onClick={props.onClose}
            className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            aria-label={t("common.actions.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="max-h-[70vh] space-y-4 overflow-y-auto px-5 py-4">
          {/* ID */}
          <div>
            <label htmlFor="af-id" className="block text-xs font-medium text-slate-700">
              {t("admin.agents.form.idLabel")}
            </label>
            <input
              id="af-id"
              type="text"
              value={id}
              onChange={(e) => setId(e.target.value)}
              readOnly={isEdit}
              required={!isEdit}
              maxLength={AGENT_ID_MAX_LEN}
              pattern={AGENT_ID_PATTERN.source}
              autoComplete="off"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm shadow-sm read-only:bg-slate-100 read-only:text-slate-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="70486"
              disabled={mutation.isPending}
            />
            <p className="mt-1 text-[11px] text-slate-400">{t("admin.agents.form.idHint")}</p>
          </div>

          {/* Official name */}
          <div>
            <label htmlFor="af-name" className="block text-xs font-medium text-slate-700">
              {t("admin.agents.form.nameLabel")}
            </label>
            <input
              id="af-name"
              type="text"
              value={officialName}
              onChange={(e) => setOfficialName(e.target.value)}
              required
              maxLength={300}
              autoComplete="off"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              disabled={mutation.isPending}
            />
          </div>

          {/* Currency */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="af-currency" className="block text-xs font-medium text-slate-700">
                {t("admin.agents.form.currencyLabel")}
              </label>
              <input
                id="af-currency"
                type="text"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                maxLength={10}
                autoComplete="off"
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm uppercase shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="USD / JPY / EUR ..."
                disabled={mutation.isPending}
              />
            </div>

            <div>
              <label htmlFor="af-priority" className="block text-xs font-medium text-slate-700">
                {t("admin.agents.form.priorityLabel")}
              </label>
              <input
                id="af-priority"
                type="number"
                min={PRIORITY_MIN}
                max={PRIORITY_MAX}
                value={priority}
                onChange={(e) => setPriority(Number(e.target.value))}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm tabular-nums shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                disabled={mutation.isPending}
              />
              <p className="mt-1 text-[11px] text-slate-400">
                {t("admin.agents.form.priorityHint")}
              </p>
            </div>
          </div>

          {/* Keywords (chip) */}
          <div>
            <label htmlFor="af-keyword" className="block text-xs font-medium text-slate-700">
              {t("admin.agents.form.keywordsLabel")}
            </label>
            <div className="mt-1 flex min-h-[40px] flex-wrap items-center gap-1.5 rounded-md border border-slate-300 px-2 py-1.5 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
              {keywords.map((kw, i) => (
                <span
                  key={`${kw}-${i}`}
                  className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-700"
                >
                  {kw}
                  <button
                    type="button"
                    onClick={() => removeKeyword(i)}
                    className="text-slate-400 transition hover:text-red-500"
                    aria-label={t("admin.agents.form.removeKeyword", { keyword: kw })}
                    disabled={mutation.isPending}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <input
                id="af-keyword"
                type="text"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={handleKeywordKeyDown}
                onBlur={handleKeywordBlur}
                placeholder={
                  keywords.length === 0 ? t("admin.agents.form.keywordsPlaceholder") : ""
                }
                className="min-w-[120px] flex-1 border-0 bg-transparent p-0 text-sm focus:outline-none focus:ring-0"
                disabled={mutation.isPending}
                autoComplete="off"
              />
            </div>
            <p className="mt-1 text-[11px] text-slate-400">{t("admin.agents.form.keywordsHint")}</p>
          </div>

          {/* Extraction rules — 폼/JSON 토글 편집기 */}
          <ExtractionRulesEditor
            mode={rulesMode}
            onModeChange={setRulesMode}
            rulesText={rulesText}
            onRulesTextChange={setRulesText}
            parseError={parsedRules.error}
            disabled={mutation.isPending}
          />

          {/* Review rules (M23_2) — 읽기 전용 + 해결됨 삭제. edit 모드에서 항목 있을 때만 노출 */}
          {isEdit && reviewEntries.length > 0 ? (
            <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50/60 p-3">
              <div>
                <span className="text-xs font-medium text-amber-800">
                  {t("admin.agents.form.reviewRulesLabel")}
                </span>
                <p className="mt-0.5 text-[11px] text-amber-700/80">
                  {t("admin.agents.form.reviewRulesHint")}
                </p>
              </div>
              <ul className="space-y-1.5">
                {reviewEntries.map(([field, entry]) => (
                  <li
                    key={field}
                    className="flex items-start gap-2 rounded-md border border-amber-200 bg-white px-2.5 py-1.5"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                          {fieldLabel(field)}
                        </span>
                        <span className="font-mono text-[10px] text-slate-400">{field}</span>
                      </div>
                      {entry?.note ? (
                        <p className="mt-1 text-xs text-slate-700">{entry.note}</p>
                      ) : null}
                      {entry?.reason ? (
                        <p className="mt-0.5 text-[11px] text-slate-400">
                          {t("admin.agents.form.reviewRulesReason")}: {entry.reason}
                        </p>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeReviewRule(field)}
                      disabled={mutation.isPending}
                      className="shrink-0 rounded-md p-1 text-amber-400 transition hover:bg-amber-100 hover:text-red-600 disabled:opacity-50"
                      aria-label={t("admin.agents.form.reviewRulesRemove", {
                        field: fieldLabel(field),
                      })}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* Flags */}
          <div className="flex flex-wrap gap-4">
            <label className="inline-flex cursor-pointer items-center gap-2 text-xs text-slate-700">
              <input
                type="checkbox"
                checked={withholdingEnabled}
                onChange={(e) => setWithholdingEnabled(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                disabled={mutation.isPending}
              />
              {t("admin.agents.form.withholdingLabel")}
            </label>
            <label className="inline-flex cursor-pointer items-center gap-2 text-xs text-slate-700">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                disabled={mutation.isPending}
              />
              {t("admin.agents.form.isActiveLabel")}
            </label>
          </div>

          {apiError ? (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
            >
              {apiError}
            </div>
          ) : null}

          <div className="flex items-center justify-between border-t border-slate-100 pt-4">
            <span className="text-[11px] text-slate-400">
              {isEdit
                ? isDirty
                  ? t("admin.agents.form.dirty", {
                      count: Object.keys(changedFields).length,
                    })
                  : t("admin.agents.form.clean")
                : ""}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={props.onClose}
                className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                disabled={mutation.isPending}
              >
                {t("common.actions.cancel")}
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {mutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                ) : null}
                {submitLabel}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
